import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { X, ArrowLeft } from "lucide-react";

export default function AGB() {
  const navigate = useNavigate();

  const close = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <div
      className="min-h-screen bg-[#FFFBFB] font-body text-qh-ink"
      data-testid="agb-page"
    >
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-qh-line">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center shrink-0" data-testid="agb-logo">
            <img
              src="/assets/qhearts-logo.png"
              alt="QHearts"
              className="h-14 sm:h-16 w-auto object-contain select-none"
              draggable="false"
            />
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={close}
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-qh-slate hover:text-qh-ink rounded-full border border-qh-line px-4 py-2 transition-colors"
              data-testid="agb-back"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück
            </button>
            <button
              onClick={close}
              className="sm:hidden h-10 w-10 rounded-full border border-qh-line bg-white flex items-center justify-center"
              aria-label="Schließen"
              data-testid="agb-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <motion.main
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-20"
      >
        <span className="text-xs uppercase tracking-[0.22em] font-semibold text-qh-rose">
          Rechtliches
        </span>
        <h1 className="mt-3 font-display font-medium text-4xl sm:text-5xl tracking-tight leading-tight">
          Allgemeine{" "}
          <span className="italic text-qh-rose">Geschäftsbedingungen</span>
        </h1>
        <p className="mt-5 text-lg text-qh-slate leading-relaxed">
          QHearts – QHearts UG (haftungsbeschränkt)
        </p>

        <article className="mt-14 space-y-12 text-[15px] sm:text-base leading-relaxed">
          <Paragraph number="§1" title="Anbieter und Geltungsbereich">
            <p>
              (1) Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für die
              Nutzung der Plattform „QHearts" (nachfolgend „Plattform"),
              betrieben durch:
            </p>
            <p className="font-semibold">
              QHearts UG (haftungsbeschränkt)
              <br />
              <span className="font-normal text-qh-slate">
                (nachfolgend „Anbieter")
              </span>
            </p>
            <p>
              (2) Die Plattform dient der Förderung authentischer
              zwischenmenschlicher Begegnungen zwischen volljährigen Personen.
            </p>
            <p>
              (3) Mit der Registrierung akzeptiert der Nutzer diese AGB
              verbindlich.
            </p>
            <p>
              (4) Abweichende Bedingungen der Nutzer finden keine Anwendung, es
              sei denn, der Anbieter stimmt ihrer Geltung ausdrücklich zu.
            </p>
          </Paragraph>

          <Paragraph number="§2" title="Leistungsbeschreibung">
            <p>
              (1) QHearts ist eine Online-Plattform (Webseite und/oder mobile
              Anwendung), über die Nutzer miteinander in Kontakt treten können.
            </p>
            <p>
              (2) Die Kontaktaufnahme erfolgt über ein systembasiertes
              Interaktionsmodell („Herzen"), das eine bewusste und
              personalisierte Kommunikation fördert.
            </p>
            <p>
              (3) Der Anbieter schuldet ausschließlich die Bereitstellung der
              technischen Infrastruktur.
            </p>
            <p>
              (4) Ein Erfolg in Form von Kontakten, Beziehungen oder realen
              Begegnungen wird ausdrücklich nicht geschuldet.
            </p>
          </Paragraph>

          <Paragraph
            number="§3"
            title="Teilnahmevoraussetzungen (Volljährigkeit & Verifikation)"
          >
            <p>
              (1) Die Nutzung der Plattform ist ausschließlich volljährigen
              Personen (mindestens 18 Jahre) gestattet.
            </p>
            <p>
              (2) Der Anbieter behält sich vor, die Volljährigkeit durch
              geeignete Maßnahmen zu überprüfen, insbesondere durch:
            </p>
            <BulletList
              items={[
                "Ausweisdokumente",
                "biometrische Verfahren",
                "Drittanbieter-Verifikationssysteme",
              ]}
            />
            <p>
              (3) Nutzer sind verpflichtet, im Rahmen der Verifikation
              wahrheitsgemäße Angaben zu machen.
            </p>
            <p>
              (4) Der Anbieter ist berechtigt, Nutzerkonten ohne vollständige
              oder erfolgreiche Verifikation einzuschränken oder zu sperren.
            </p>
            <p>(5) Eine Nutzung durch Minderjährige ist strikt untersagt.</p>
          </Paragraph>

          <Paragraph number="§4" title="Registrierung und Nutzerkonto">
            <p>
              (1) Die Nutzung der Plattform setzt die Erstellung eines
              Nutzerkontos voraus.
            </p>
            <p>(2) Pro Person ist nur ein Konto zulässig.</p>
            <p>(3) Nutzer sind verpflichtet:</p>
            <BulletList
              items={[
                "wahrheitsgemäße Angaben zu machen",
                "ihre Zugangsdaten geheim zu halten",
                "ihr Konto nicht Dritten zu überlassen",
              ]}
            />
            <p>
              (4) Der Anbieter ist berechtigt, Konten bei Verdacht auf
              Missbrauch, falsche Angaben oder Mehrfachregistrierung zu sperren.
            </p>
          </Paragraph>

          <Paragraph number="§5" title="Leistungen und Funktionen">
            <p>
              (1) Nutzer können Profile erstellen, Inhalte hochladen und andere
              Profile einsehen.
            </p>
            <p>
              (2) Die Kontaktaufnahme erfolgt über sogenannte „Herzen", die mit
              individuellen Nachrichten versehen werden können.
            </p>
            <p>
              (3) Der Anbieter behält sich vor, Funktionen jederzeit zu ändern,
              zu erweitern oder einzuschränken, sofern dies für den Nutzer
              zumutbar ist.
            </p>
          </Paragraph>

          <Paragraph number="§6" title="Kostenpflichtige Leistungen und Abonnements">
            <p>(1) Teile der Plattform sind kostenpflichtig.</p>
            <p>
              (2) Der Erwerb von „Herzen" erfolgt über kostenpflichtige
              Abonnements.
            </p>
            <p>
              (3) Preise und Leistungen werden vor Vertragsschluss transparent
              dargestellt.
            </p>
            <p>
              (4) Abonnements verlängern sich automatisch, sofern sie nicht
              fristgerecht gekündigt werden.
            </p>
            <p>
              (5) Kündigungen sind jederzeit zum Ende der jeweiligen Laufzeit
              möglich.
            </p>
            <p>
              (6) Im Falle von Zahlungsverzug ist der Anbieter berechtigt,
              Leistungen einzuschränken.
            </p>
          </Paragraph>

          <Paragraph number="§7" title="Widerrufsrecht (digitale Inhalte)">
            <p>
              (1) Verbrauchern steht grundsätzlich ein gesetzliches
              Widerrufsrecht zu.
            </p>
            <p>
              (2) Bei digitalen Inhalten (z. B. sofort nutzbare Herzen) kann das
              Widerrufsrecht vorzeitig erlöschen, wenn:
            </p>
            <BulletList
              items={[
                "der Nutzer ausdrücklich zustimmt, dass die Leistung sofort beginnt, und",
                "bestätigt, dass er dadurch sein Widerrufsrecht verliert",
              ]}
            />
          </Paragraph>

          <Paragraph number="§8" title="Verhaltenspflichten der Nutzer">
            <p>
              (1) Nutzer verpflichten sich zu respektvollem und
              gesetzeskonformem Verhalten.
            </p>
            <p>(2) Untersagt sind insbesondere:</p>
            <BulletList
              items={[
                "Beleidigungen, Diskriminierung oder Belästigung",
                "sexuelle Belästigung oder unangemessene Inhalte",
                "Fake-Profile oder Identitätstäuschung",
                "gewerbliche Nutzung ohne Zustimmung",
                "Verbreitung rechtswidriger Inhalte",
              ]}
            />
            <p>
              (3) Der Anbieter ist berechtigt, Inhalte zu entfernen und Konten
              zu sperren.
            </p>
          </Paragraph>

          <Paragraph number="§9" title="Haftung und Eigenverantwortung">
            <p>
              (1) Der Anbieter stellt lediglich die Plattform zur Verfügung und
              ist nicht an der Kommunikation oder an Treffen zwischen Nutzern
              beteiligt.
            </p>
            <p>(2) Nutzer handeln eigenverantwortlich, insbesondere bei:</p>
            <BulletList
              items={[
                "der Kontaktaufnahme",
                "der Weitergabe persönlicher Daten",
                "der Organisation und Durchführung realer Treffen",
              ]}
            />
            <p>(3) Der Anbieter übernimmt keine Haftung für:</p>
            <BulletList
              items={[
                "das Verhalten anderer Nutzer",
                "die Richtigkeit von Nutzerangaben",
                "Schäden, die aus realen Begegnungen resultieren",
              ]}
            />
            <p>
              (4) Nutzer sind verpflichtet, bei Treffen mit anderen Nutzern
              angemessene Vorsichtsmaßnahmen zu treffen.
            </p>
            <p>(5) Der Anbieter haftet unbeschränkt nur bei:</p>
            <BulletList
              items={[
                "Vorsatz",
                "grober Fahrlässigkeit",
                "Verletzung von Leben, Körper oder Gesundheit",
              ]}
            />
            <p>
              (6) Im Übrigen ist die Haftung ausgeschlossen, soweit gesetzlich
              zulässig.
            </p>
          </Paragraph>

          <Paragraph number="§10" title="Inhalte der Nutzer">
            <p>(1) Nutzer sind für ihre Inhalte selbst verantwortlich.</p>
            <p>
              (2) Der Nutzer räumt dem Anbieter ein einfaches Nutzungsrecht ein,
              Inhalte im Rahmen der Plattform darzustellen.
            </p>
            <p>
              (3) Der Anbieter ist nicht verpflichtet, Inhalte aktiv zu
              überwachen.
            </p>
          </Paragraph>

          <Paragraph number="§11" title="Datenschutz und Datenverarbeitung">
            <p>
              (1) Der Schutz personenbezogener Daten hat für den Anbieter
              höchste Priorität.
            </p>
            <p>
              (2) Die Verarbeitung personenbezogener Daten erfolgt ausschließlich
              im Rahmen der geltenden Datenschutzgesetze, insbesondere der
              DSGVO.
            </p>
            <p>
              (3) Details zur Verarbeitung personenbezogener Daten ergeben sich
              aus der gesonderten Datenschutzerklärung.
            </p>
            <p>
              (4) Im Rahmen der Nutzung können insbesondere folgende Daten
              verarbeitet werden:
            </p>
            <BulletList
              items={[
                "Profildaten",
                "Kommunikationsinhalte",
                "Verifizierungsdaten (z. B. Ausweisdaten, biometrische Daten)",
                "Nutzungsdaten",
              ]}
            />
            <p>
              (5) Verifizierungs- und Sicherheitsmaßnahmen dienen dem Schutz der
              Nutzer und der Authentizität der Plattform.
            </p>
          </Paragraph>

          <Paragraph number="§12" title="Sicherheitsmaßnahmen">
            <p>
              (1) QHearts setzt technische und organisatorische Maßnahmen ein,
              um:
            </p>
            <BulletList
              items={[
                "Fake-Profile zu vermeiden",
                "Nutzer zu schützen",
                "Missbrauch zu verhindern",
              ]}
            />
            <p>
              (2) Der Anbieter kann Sicherheitsprüfungen durchführen und Nutzer
              zur Mitwirkung verpflichten.
            </p>
          </Paragraph>

          <Paragraph number="§13" title="Vertragslaufzeit und Kündigung">
            <p>(1) Der Nutzungsvertrag läuft auf unbestimmte Zeit.</p>
            <p>(2) Nutzer können ihr Konto jederzeit kündigen.</p>
            <p>
              (3) Der Anbieter kann Konten aus wichtigem Grund kündigen,
              insbesondere bei Verstößen gegen diese AGB.
            </p>
          </Paragraph>

          <Paragraph number="§14" title="Änderungen der AGB">
            <p>(1) Der Anbieter behält sich vor, diese AGB anzupassen.</p>
            <p>(2) Nutzer werden über Änderungen informiert.</p>
            <p>
              (3) Erfolgt kein Widerspruch innerhalb von 14 Tagen, gelten die
              Änderungen als angenommen.
            </p>
          </Paragraph>

          <Paragraph number="§15" title="Schlussbestimmungen">
            <p>(1) Es gilt das Recht der Bundesrepublik Deutschland.</p>
            <p>
              (2) Gerichtsstand ist – soweit gesetzlich zulässig – der Sitz des
              Anbieters.
            </p>
            <p>
              (3) Sollten einzelne Bestimmungen unwirksam sein, bleibt die
              Wirksamkeit der übrigen unberührt.
            </p>
          </Paragraph>
        </article>

        <div className="mt-20 pt-8 border-t border-qh-line text-sm text-qh-slate">
          <p>
            <span className="font-semibold text-qh-ink">Stand:</span>{" "}
            {new Date().toLocaleDateString("de-DE", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
          <p className="mt-1">QHearts UG (haftungsbeschränkt)</p>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row gap-3">
          <button
            onClick={close}
            className="rounded-full bg-qh-ink hover:bg-qh-ink/90 text-white h-12 px-8 text-sm font-medium transition-all hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
            data-testid="agb-close-bottom"
          >
            <ArrowLeft className="w-4 h-4" /> Zurück zur Seite
          </button>
          <Link
            to="/"
            className="rounded-full border border-qh-line bg-white hover:bg-[#FFFBFB] h-12 px-8 text-sm font-medium transition-colors inline-flex items-center justify-center"
          >
            Zur Startseite
          </Link>
        </div>
      </motion.main>
    </div>
  );
}

function Paragraph({ number, title, children }) {
  return (
    <section className="scroll-mt-24" id={number.replace("§", "paragraf-")}>
      <div className="flex items-baseline gap-3">
        <span className="font-display text-qh-rose text-2xl sm:text-3xl font-medium">
          {number}
        </span>
        <h2 className="font-display text-qh-ink text-2xl sm:text-3xl font-medium leading-tight">
          {title}
        </h2>
      </div>
      <div className="mt-5 space-y-4 text-qh-ink/90">{children}</div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className="pl-0 space-y-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-qh-rose shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}
